import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { ROLE_HOME } from '../../utils/ProtectedRoute';
import { Eye, EyeOff, School } from 'lucide-react';
import api from '../../services/api';

export default function LoginPage() {
  const { signIn, signInWithSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Route decides which login experience to show: /schooladmin = email login,
  // /teacher-login = teacher ID login, everything else (/login) = student ID login.
  const isSchoolAdminRoute = location.pathname === '/schooladmin';
  const isTeacherRoute = location.pathname === '/teacher-login';

  // Derived directly from the route (not state) so switching portals via the
  // in-page links updates the form without needing a full remount.
  const loginMethod = isSchoolAdminRoute ? 'email' : 'id';
  const [email, setEmail] = useState('');
  const [studentId, setStudentId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load saved email/ID from localStorage on mount (only relevant to the
  // matching route, so we don't flip the user into the wrong login mode).
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedId = localStorage.getItem('rememberedId');

    if (isSchoolAdminRoute && savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
    } else if (!isSchoolAdminRoute && savedId) {
      setStudentId(savedId);
      setRememberMe(true);
    }
  }, [isSchoolAdminRoute]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (loginMethod === 'id') {
        // ID-based login (students/teachers)
        const res = await api.post('/auth/login-with-id', {
          id: studentId.trim().toUpperCase(),
          password,
        });
        
        // Save or remove ID based on "Remember me" checkbox
        if (rememberMe) {
          localStorage.setItem('rememberedId', studentId.trim().toUpperCase());
        } else {
          localStorage.removeItem('rememberedId');
        }
        
        // Use Supabase's built-in session management (secure)
        if (res.data.session) {
          const result = await signInWithSession(res.data.session);
          const role = result.profile?.role || res.data.role;
          const roleMap = {
            student: '/student/academic-records',
            teacher: '/teacher',
          };
          const from = location.state?.from?.pathname;
          navigate(from || roleMap[role] || ROLE_HOME[role] || '/admin', { replace: true });
        }
      } else {
        // Email-based login (school admins)
        // Save or remove email based on "Remember me" checkbox
        if (rememberEmail) {
          localStorage.setItem('rememberedEmail', email.trim());
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        const result = await signIn(email.trim(), password);
        const from = location.state?.from?.pathname;
        navigate(from || ROLE_HOME[result.profile?.role] || '/admin', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-sand-100 flex flex-col items-center justify-center px-4">
      {/* Card */}
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-gold flex items-center justify-center shadow-card mb-4">
            <School className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-charcoal-900">Skoolstak</h1>
          <p className="text-sm text-charcoal-500 mt-1">School Management System</p>
        </div>

        <div className="card">
          <span className="inline-block mb-3 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase bg-brand-gold/10 text-brand-gold-dark">
            {isSchoolAdminRoute ? 'School Admin Portal' : isTeacherRoute ? 'Teacher Portal' : 'Student Portal'}
          </span>
          <h2 className="text-xl font-bold text-charcoal-900 mb-1">Welcome back</h2>
          <p className="text-sm text-charcoal-500 mb-6">
            {isSchoolAdminRoute
              ? 'Sign in to your school admin account'
              : isTeacherRoute
                ? 'Sign in to your teacher account'
                : 'Sign in to your student account'}
          </p>

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 text-danger text-sm rounded-xl">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email or ID Input */}
            {loginMethod === 'email' ? (
              <div>
                <label className="label" htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="your@school.edu.gh"
                />
              </div>
            ) : (
              <div>
                <label className="label" htmlFor="studentId">{isTeacherRoute ? 'Teacher ID' : 'Student ID'}</label>
                <input
                  id="studentId"
                  type="text"
                  required
                  value={studentId}
                  onChange={e => setStudentId(e.target.value)}
                  className="input-field uppercase"
                  placeholder={isTeacherRoute ? 'TEA-2024-001' : 'STU-2024-001'}
                />
                <p className="text-xs text-charcoal-400 mt-1">
                  Your ID can be found on your {isTeacherRoute ? 'staff' : 'student'} card
                </p>
                
                {/* First-time login notice */}
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-xs text-blue-900">
                    <span className="font-semibold">Your password is the same as your {isTeacherRoute ? 'Teacher' : 'Student'} ID.</span> You'll be able to change it after logging in.
                  </p>
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="label" htmlFor="password">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input-field pr-11"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-400 hover:text-charcoal-700"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Remember me checkbox for email login */}
            {loginMethod === 'email' && (
              <div className="flex items-center">
                <input
                  id="rememberEmail"
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={e => setRememberEmail(e.target.checked)}
                  className="w-4 h-4 text-green-600 border-charcoal-300 rounded focus:ring-green-500 focus:ring-2"
                />
                <label htmlFor="rememberEmail" className="ml-2 text-sm text-charcoal-600">
                  Remember my email for faster login
                </label>
              </div>
            )}

            {/* Remember me checkbox for ID login */}
            {loginMethod === 'id' && (
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-green-600 border-charcoal-300 rounded focus:ring-green-500 focus:ring-2"
                />
                <label htmlFor="rememberMe" className="ml-2 text-sm text-charcoal-600">
                  Remember my ID for faster login
                </label>
              </div>
            )}

            {/* Forgot password (school admins, teachers and students) */}
            {(
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-brand-gold hover:text-brand-gold-dark font-medium"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading && (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>

        {isSchoolAdminRoute && (
          <p className="text-center text-xs text-charcoal-400 mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-brand-gold hover:text-brand-gold-dark font-medium">
              Register your school
            </Link>
          </p>
        )}

        {/* Portal switcher */}
        <div className="mt-4 flex items-center justify-center gap-2 text-xs">
          <span className="text-charcoal-400">Wrong portal?</span>
          {!isSchoolAdminRoute && (
            <>
              {!isTeacherRoute && (
                <Link to="/teacher-login" className="px-2.5 py-1 rounded-full bg-sand-100 text-charcoal-600 hover:bg-sand-200 font-medium">
                  Teacher
                </Link>
              )}
              {isTeacherRoute && (
                <Link to="/login" className="px-2.5 py-1 rounded-full bg-sand-100 text-charcoal-600 hover:bg-sand-200 font-medium">
                  Student
                </Link>
              )}
            </>
          )}
          {!isSchoolAdminRoute && (
            <Link to="/schooladmin" className="px-2.5 py-1 rounded-full bg-sand-100 text-charcoal-600 hover:bg-sand-200 font-medium">
              School Admin
            </Link>
          )}
          {isSchoolAdminRoute && (
            <>
              <Link to="/login" className="px-2.5 py-1 rounded-full bg-sand-100 text-charcoal-600 hover:bg-sand-200 font-medium">
                Student
              </Link>
              <Link to="/teacher-login" className="px-2.5 py-1 rounded-full bg-sand-100 text-charcoal-600 hover:bg-sand-200 font-medium">
                Teacher
              </Link>
            </>
          )}
        </div>

        <p className="text-center text-xs text-charcoal-400 mt-4">
          &copy; {new Date().getFullYear()} Skoolstak &middot; Built for Ghana 🇬🇭 and Africa.
        </p>
      </div>
    </div>
  );
}
